import { useMutation, useQueryClient } from '@tanstack/react-query';

import { authClient } from '../auth/client';

/**
 * Hook to set or clear the active organization for the current user.
 *
 * @example
 * ```tsx
 * const { mutate: setActive, isPending } = useSetActiveOrganization();
 *
 * // Set an active organization
 * setActive('org-id-123');
 *
 * // Clear the active organization (pass null or undefined)
 * setActive(null);
 * setActive(); // Also clears
 * ```
 */
export function useSetActiveOrganization() {
  const queryClient = useQueryClient();

  return useMutation({
    /**
     * Sets the active organization for the current user.
     *
     * @param organizationId - The ID of the organization to set as active.
     *                         Pass `null` or `undefined` to clear the active organization.
     */
    mutationFn: async (organizationId?: string | null) => {
      const result = await authClient.organization.setActive({
        organizationId: organizationId ?? null,
      });

      if (result.error) {
        throw new Error(result.error.message || 'Failed to set active organization');
      }

      return result.data;
    },
    // Use onSettled to always refetch session state after the mutation,
    // regardless of success or failure, to stay in sync with the server.
    onSettled: () => {
      // Invalidate session to refetch with updated active organization
      void queryClient.invalidateQueries({ queryKey: ['auth'] });
      // Invalidate organization queries (active org data + allowed-organizations list)
      void queryClient.invalidateQueries({ queryKey: ['organization'] });
      // Invalidate permissions since scope depends on active organization
      void queryClient.invalidateQueries({ queryKey: ['permissions'] });
      // Invalidate brands query cache since brands are organization-scoped
      void queryClient.invalidateQueries({ queryKey: ['brands'] });
      // Invalidate product lines query cache since product lines are organization-scoped
      void queryClient.invalidateQueries({ queryKey: ['product-lines'] });
      // Invalidate locations query cache since locations are organization-scoped
      void queryClient.invalidateQueries({ queryKey: ['locations'] });
      // Invalidate all asset group queries since they are organization-scoped
      void queryClient.invalidateQueries({ queryKey: ['assetGroup'] });
      // The unconfirmed-groups list is scoped to the active organization for users
      void queryClient.invalidateQueries({ queryKey: ['asset-group-unconfirmed'] });
      // Invalidate AI metrics since they are organization-scoped
      void queryClient.invalidateQueries({ queryKey: ['ai-metrics'] });
    },
  });
}
